import { useEffect, useState } from "react";
import { Bed, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { BOOKING_URL } from "@/constants";
import { optimizeImg } from "@/lib/img";

const RoomSlideshow = ({ images, eager }) => {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const i = setInterval(
      () => setIdx((p) => (p + 1) % images.length),
      9000
    );
    return () => clearInterval(i);
  }, [images.length]);

  return (
    <div className="slideshow-container" data-testid="room-slideshow">
      {images.map((img, i) => (
        <img
          key={img.url}
          src={optimizeImg(img.url, 1200)}
          srcSet={`${optimizeImg(img.url, 600)} 600w, ${optimizeImg(img.url, 1000)} 1000w, ${optimizeImg(img.url, 1400)} 1400w`}
          sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 600px"
          alt={img.alt}
          loading={eager && i === 0 ? "eager" : "lazy"}
          decoding="async"
          className={`slideshow-image ${i === idx ? "active" : ""}`}
        />
      ))}
      <div className="slideshow-dots">
        {images.map((img, i) => (
          <button
            key={`dot-${img.url}`}
            className={`slideshow-dot ${i === idx ? "active" : ""}`}
            onClick={() => setIdx(i)}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

const RoomVideo = ({ src, poster, alt }) => (
  <div className="slideshow-container" data-testid="room-video">
    {/* Poster shows instantly; video streams in. preload=metadata keeps bandwidth low. */}
    <video
      src={src}
      poster={poster}
      autoPlay
      muted
      loop
      playsInline
      preload="metadata"
      aria-label={alt}
      className="absolute inset-0 w-full h-full object-cover"
    />
  </div>
);

const RoomCard = ({ room, selected, onToggle, eager }) => (
  <Card
    className="room-card border-0 overflow-hidden"
    data-testid={`room-card-${room.id}`}
  >
    {room.video ? (
      <RoomVideo
        src={room.video}
        poster={optimizeImg(room.images[0]?.url, 1000)}
        alt={room.name}
      />
    ) : (
      <RoomSlideshow images={room.images} eager={eager} />
    )}
    <CardContent className="p-6">
      <h3 className="font-['Playfair_Display'] text-2xl font-bold text-[#1E407C] mb-2">
        {room.name}
      </h3>
      <div className="flex items-center gap-4 text-gray-500 text-sm mb-4">
        <span className="flex items-center gap-1">
          <Bed size={16} /> {room.bed_type}
        </span>
        <span className="flex items-center gap-1">
          <Users size={16} /> Up to {room.max_guests}
        </span>
      </div>
      <p className="text-gray-600 text-sm mb-4 line-clamp-3">
        {room.description}
      </p>
      <Button
        variant="link"
        className="p-0 text-[#D4AF37] font-semibold mb-4"
        onClick={onToggle}
        data-testid={`room-toggle-${room.id}`}
      >
        {selected ? "Hide ↑" : "View Details ↓"}
      </Button>
      {selected && (
        <div className="mt-4 pt-4 border-t">
          <h4 className="font-semibold text-[#1E407C] mb-3">
            In-Room Amenities:
          </h4>
          <ul className="space-y-2">
            {room.amenities.map((a) => (
              <li
                key={a}
                className="flex items-start gap-2 text-sm text-gray-600"
              >
                <span className="text-[#D4AF37]">•</span>
                {a}
              </li>
            ))}
          </ul>
        </div>
      )}
      <a
        href={BOOKING_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`room-book-${room.id}`}
      >
        <Button className="w-full bg-[#1E407C] hover:bg-[#153060] text-white mt-2">
          Check Availability
        </Button>
      </a>
    </CardContent>
  </Card>
);

export const RoomsSection = ({ rooms }) => {
  const [sel, setSel] = useState(null);

  return (
    <section
      id="rooms"
      className="py-20 bg-[#F8F9FA]"
      data-testid="rooms-section"
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-16">
          <p className="text-[#D4AF37] font-semibold uppercase tracking-wider mb-3">
            Accommodations
          </p>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-5xl font-bold text-[#1E407C] mb-4">
            Our Rooms & Suites
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {rooms.map((r, i) => (
            <RoomCard
              key={r.id}
              room={r}
              selected={sel === r.id}
              onToggle={() => setSel(sel === r.id ? null : r.id)}
              eager={i < 2}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
